import React, { useMemo, useState } from "react";
import { RSD } from "../../lib/api";
import { fmtDuration, fmtTime } from "../../hooks/useShift";
import { ShiftMoneyCards, ShiftCoinsCard, ShiftSourceBreakdown, CashReconciliation, showCash } from "./ShiftCards";
import { X, Play, Square, AlertTriangle } from "lucide-react";
// Shift start is the most reliable kitchen user gesture → use it to unlock notification audio.
// Audio never blocks or delays the backend-authoritative shift start.
import { playNewOrderBell, unlockAudio } from "../../lib/kitchenAudio";

function Sheet({ title, kicker, onClose, children, footer, testid }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" data-testid={testid}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-[#F6F7F9] w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[92vh] flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b bg-white rounded-t-3xl">
          <div>
            {kicker && <p className="text-[10px] font-black uppercase tracking-[0.14em] text-brand-red">{kicker}</p>}
            <h3 className="font-display font-black text-xl">{title}</h3>
          </div>
          <button data-testid={`${testid}-close`} onClick={onClose}
            className="w-11 h-11 -mr-2 flex items-center justify-center rounded-full hover:bg-slate-100"><X size={20} /></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto space-y-4">{children}</div>
        <div className="px-5 py-4 border-t bg-white">{footer}</div>
      </div>
    </div>
  );
}

export function StartShiftModal({ paymentMethod, busy, onClose, onStart, showOpeningCash = true }) {
  const [cash, setCash] = useState("");
  return (
    <Sheet testid="start-shift-modal" kicker="Početak smene" title="Pokretanje nove smene" onClose={onClose}
      footer={
        <button data-testid="start-shift-confirm" disabled={busy}
          onClick={() => { unlockAudio().then((ok) => ok && playNewOrderBell()).catch(() => {}); onStart(showOpeningCash ? cash : 0); }}
          className="w-full rounded-2xl bg-brand-red hover:bg-brand-redHover text-white py-4 font-display font-black text-lg flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.99] transition-transform">
          <Play size={18} />POČNI SMENU
        </button>}>
      {showOpeningCash && showCash(paymentMethod) && (
        <label className="block bg-white rounded-2xl border p-4">
          <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">Početno stanje gotovine (opciono)</span>
          <div className="flex items-baseline gap-2 mt-1">
            <input data-testid="start-shift-opening-cash" value={cash} inputMode="decimal"
              onChange={(e) => setCash(e.target.value.replace(/[^\d.,]/g, "").replace(",", "."))}
              placeholder="5000"
              className="flex-1 rounded-xl border-2 border-slate-200 px-3 py-3 text-2xl font-display font-black" />
            <span className="font-black text-slate-400">RSD</span>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Gotovina koja je već u kasi pre početka prodaje. Ne ulazi u promet restorana.
          </p>
        </label>
      )}
      <p className="text-sm text-slate-500">
        {showOpeningCash
          ? "Nova smena počinje kao nov finansijski period — bez prenosa prethodnih iznosa. Vreme početka određuje server."
          : "Smena počinje odmah i otvara Vistu. Vreme početka određuje server."}
      </p>
    </Sheet>
  );
}

export function EndShiftModal({ shift, paymentMethod, busy, onClose, onEnd, showFinancials = true }) {
  const [counted, setCounted] = useState("");
  const [confirming, setConfirming] = useState(false);
  const cashMode = showFinancials && showCash(paymentMethod);
  const parsed = counted === "" ? null : Number(counted);
  const difference = useMemo(
    () => (parsed === null || Number.isNaN(parsed) ? null : Math.round((parsed - (shift.expected_drawer || 0)) * 100) / 100),
    [parsed, shift.expected_drawer]);
  // Server-reported count only; the backend re-checks at submit time anyway.
  const blockingOrders = shift.active_vista_orders || [];
  const blockedCount = shift.active_vista_order_count ?? 0;
  const blocked = blockedCount > 0;

  return (
    <Sheet testid="end-shift-modal" kicker="Završetak smene" title="Pregled i zatvaranje" onClose={onClose}
      footer={
        blocked ? (
          <div data-testid="end-shift-blocked" className="space-y-2">
            <p className="flex items-center gap-2 text-sm font-bold text-brand-red">
              <AlertTriangle size={16} className="shrink-0" />
              {blockedCount === 1
                ? "Postoji 1 aktivna Vista porudžbina. Završite je pre zatvaranja smene."
                : (blockedCount % 10 >= 2 && blockedCount % 10 <= 4 && ![12, 13, 14].includes(blockedCount % 100))
                  ? `Postoje ${blockedCount} aktivne Vista porudžbine. Završite ih pre zatvaranja smene.`
                  : `Postoji ${blockedCount} aktivnih Vista porudžbina. Završite ih pre zatvaranja smene.`}            </p>
            <details data-testid="end-shift-blocking-list">
              <summary className="text-xs font-black uppercase tracking-wider text-slate-400 cursor-pointer">Prikaži porudžbine</summary>
              <div className="mt-1.5 space-y-1">
                {blockingOrders.map((o) => (
                  <div key={o.id} data-testid={`blocking-order-${o.id}`} className="flex items-center justify-between text-sm bg-[#FAFAF8] rounded-xl px-2.5 py-2">
                    <span className="font-black">ŠIFRA {o.public_code}</span>
                    <span className="text-xs uppercase text-slate-500">{o.status}</span>
                  </div>
                ))}
              </div>
            </details>
          </div>
        ) : !confirming ? (
          <button data-testid="end-shift-review" disabled={busy} onClick={() => setConfirming(true)}
            className="w-full rounded-2xl bg-slate-900 text-white py-4 font-display font-black text-lg flex items-center justify-center gap-2 disabled:opacity-50">
            <Square size={16} />ZAVRŠI SMENU
          </button>
        ) : (
          <div className="space-y-2">
            <p data-testid="end-shift-confirm-text" className="text-sm font-bold text-center">
              Da li ste sigurni da želite da završite smenu?
            </p>
            <div className="flex gap-2">
              <button data-testid="end-shift-cancel" onClick={() => setConfirming(false)}
                className="flex-1 rounded-2xl bg-white border-2 border-slate-200 py-3.5 font-display font-black">NE</button>
              <button data-testid="end-shift-confirm" disabled={busy} onClick={() => onEnd(counted)}
                className="flex-1 rounded-2xl bg-brand-red hover:bg-brand-redHover text-white py-3.5 font-display font-black disabled:opacity-50">DA, ZAVRŠI</button>
            </div>
          </div>
        )}>
      <div className="bg-white rounded-2xl border p-3 grid grid-cols-3 gap-2 text-center">
        {[["Početak", fmtTime(shift.started_at)], ["Kraj", "sada"], ["Trajanje", fmtDuration(shift.started_at)]].map(([l, v]) => (
          <div key={l}>
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">{l}</p>
            <p className="font-display font-black text-lg">{v}</p>
          </div>
        ))}
      </div>

      {showFinancials && (
        <>
          <ShiftMoneyCards shift={shift} paymentMethod={paymentMethod} big />
          <ShiftCoinsCard shift={shift} />
          <ShiftSourceBreakdown shift={shift} />
        </>
      )}

      {cashMode && (
        <div className="space-y-2">
          <label className="block bg-white rounded-2xl border p-4">
            <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">Prebrojana gotovina</span>
            <div className="flex items-baseline gap-2 mt-1">
              <input data-testid="end-shift-counted-cash" value={counted} inputMode="decimal"
                onChange={(e) => setCounted(e.target.value.replace(/[^\d.,-]/g, "").replace(",", "."))}
                placeholder={String(Math.round(shift.expected_drawer))}
                className="flex-1 rounded-xl border-2 border-slate-200 px-3 py-3 text-2xl font-display font-black" />
              <span className="font-black text-slate-400">RSD</span>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Početno stanje {RSD(shift.opening_cash_amount)} + gotovinska prodaja {RSD(shift.cash_sales)}
            </p>
          </label>
          <CashReconciliation expected={shift.expected_drawer} counted={parsed} difference={difference} />
          <p className="text-xs text-slate-400">Kartično plaćanje se ne broji u kasi i prikazuje se odvojeno.</p>
        </div>
      )}
    </Sheet>
  );
}
